import { translate } from '@vimeo/i18n-runtime';

import { isDate } from '../is';

type DateValues = {
  seconds: number;
  minutes: number;
  hours: number;
  weekday: number;
  day: number;
  month: number;
  year: number;
};

type TimeBetweenOptions = {
  shorten?: boolean;
};

const ONE_SEC_MS = 1000;
const ONE_MIN_MS = 60 * ONE_SEC_MS;
const ONE_HR_MS = 60 * ONE_MIN_MS;
const ONE_DAY_MS = ONE_HR_MS * 24;
const MONTH_30_DAYS = ONE_DAY_MS * 30;
const ONE_YEAR_MS = 365 * ONE_DAY_MS;

/*
 * Get the number of days in a given month and year
 */
export function getDaysInMonth(month: number, year: number) {
  // 0 for date will get the last date value of the previous month
  // so we add one to month to target the correct month
  return new Date(year, month + 1, 0).getDate();
}

/*
 * Returns the relative time between two dates e.g. '2 years ago'
 */
export function timeBetween(from: Date, to: Date, timeBetweenOptions?: TimeBetweenOptions) {
  const shorten = timeBetweenOptions ? timeBetweenOptions.shorten : false;
  if (!isDate(from) || !isDate(to)) {
    throw new Error(
      `timeBetween expected Dates, received from: ${Object.prototype.toString.call(
        from,
      )} to:  ${Object.prototype.toString.call(to)}`,
    );
  }

  const created = Number(from);
  const now = Number(to);
  const diffMs = now - created;
  let d: string | number = '';

  if (diffMs < ONE_MIN_MS) {
    d = Math.round(diffMs / ONE_SEC_MS);
    if (shorten) {
      return translate('just now');
    }
    return translate({
      singular: '{COUNT} second ago',
      plural: '{COUNT} seconds ago',
      count: d,
      replacements: { COUNT: d },
    });
  } else if (diffMs < ONE_HR_MS) {
    d = Math.round(diffMs / ONE_MIN_MS);
    if (shorten) {
      return translate({
        singular: '{COUNT} min',
        plural: '{COUNT} mins',
        count: d,
        replacements: { COUNT: d },
      });
    }
    return translate({
      singular: '{COUNT} minute ago',
      plural: '{COUNT} minutes ago',
      count: d,
      replacements: { COUNT: d },
    });
  } else if (diffMs < ONE_DAY_MS) {
    d = Math.round(diffMs / ONE_HR_MS);
    if (shorten) {
      return translate({
        singular: '{COUNT} hr',
        plural: '{COUNT} hrs',
        count: d,
        replacements: { COUNT: d },
      });
    }
    return translate({
      singular: '{COUNT} hour ago',
      plural: '{COUNT} hours ago',
      count: d,
      replacements: { COUNT: d },
    });
  } else if (diffMs < MONTH_30_DAYS) {
    d = Math.round(diffMs / ONE_DAY_MS);
    if (shorten) {
      return translate({
        singular: '{COUNT} day',
        plural: '{COUNT} days',
        count: d,
        replacements: { COUNT: d },
      });
    }
    return translate({
      singular: '{COUNT} day ago',
      plural: '{COUNT} days ago',
      count: d,
      replacements: { COUNT: d },
    });
  } else if (diffMs < ONE_YEAR_MS) {
    d = Math.floor(diffMs / MONTH_30_DAYS);
    if (shorten) {
      return translate({
        singular: '{COUNT} month',
        plural: '{COUNT} months',
        count: d,
        replacements: { COUNT: d },
      });
    }
    return translate({
      singular: '{COUNT} month ago',
      plural: '{COUNT} months ago',
      count: d,
      replacements: { COUNT: d },
    });
  } else {
    d = Math.floor(diffMs / ONE_YEAR_MS);
    if (shorten) {
      return translate({
        singular: '{COUNT} year',
        plural: '{COUNT} years',
        count: d,
        replacements: { COUNT: d },
      });
    }
    return translate({
      singular: '{COUNT} year ago',
      plural: '{COUNT} years ago',
      count: d,
      replacements: { COUNT: d },
    });
  }
}

/*
 * Returns relative time from now e.g. '5 minutes ago'
 */
export function fromNow(from: Date, timeBetweenOptions?: TimeBetweenOptions) {
  return timeBetween(from, new Date(Date.now()), timeBetweenOptions);
}

export const getFormattedStringFromDateObj = (
  dateObj: Date,
  format: 'MMM DD, YYYY' | 'YYYY-MM-DD' | 'YYYY' | 'MMM' | 'MMM, YYYY' | 'MMM YYYY' | 'MMM DD',
): string | undefined => {
  const monthNames = [
    translate('Jan'),
    translate('Feb'),
    translate('Mar'),
    translate('Apr'),
    translate('May'),
    translate('Jun'),
    translate('Jul'),
    translate('Aug'),
    translate('Sep'),
    translate('Oct'),
    translate('Nov'),
    translate('Dec'),
  ];

  switch (format) {
    case 'YYYY':
      return `${dateObj.getFullYear()}`;
    case 'MMM':
      return `${monthNames[dateObj.getMonth()]}`;
    case 'MMM, YYYY':
      return `${monthNames[dateObj.getMonth()]}, ${dateObj.getFullYear()}`;
    case 'MMM YYYY':
      return `${monthNames[dateObj.getMonth()]} ${dateObj.getFullYear()}`;
    case 'YYYY-MM-DD':
      return (
        dateObj.getFullYear() +
        '-' +
        ('0' + (dateObj.getMonth() + 1)).slice(-2) +
        '-' +
        ('0' + dateObj.getDate()).slice(-2)
      );
    case 'MMM DD, YYYY':
      return `${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`;
    case 'MMM DD':
      return `${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}`;
    default:
      throw new Error('[VA] Unknown String Format Requested');
  }
};

export const getDateObjFromFormat = (dateString: string, format: 'YYYY-MM-DD'): Date => {
  if (!dateString) {
    throw new Error('Empty Date String');
  }

  switch (format) {
    case 'YYYY-MM-DD':
      const dateSegments = dateString.split('-');
      return new Date(+dateSegments[0], +dateSegments[1] - 1, +dateSegments[2]);
    default:
      throw new Error('Unsupported Format');
  }
};

export const secondsToDisplay = (seconds: number): string => {
  const hourInSeconds = 3600;
  const date = new Date(seconds * 1000).toISOString();

  if (seconds >= hourInSeconds) {
    return date.substr(11, 8);
  }

  return date.substr(14, 5);
};
